import { uKey, uGet, uSet } from "../core/storage";
import type { PracticeRecord } from "./types";

// Per-user practice dataset. Written straight to localStorage (not via uSet) so a
// quota error surfaces to the UI instead of being silently swallowed.
const KEY = "practice.records";
const PREFS = "practice.prefs";

export function newId(): string {
  return "p_" + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
}

export function loadRecords(): PracticeRecord[] {
  try {
    const arr = JSON.parse(localStorage.getItem(uKey(KEY)) || "[]");
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

export function saveRecords(recs: PracticeRecord[]): boolean {
  try {
    localStorage.setItem(uKey(KEY), JSON.stringify(recs));
    return true;
  } catch {
    return false;
  }
}

export function upsertRecord(rec: PracticeRecord): boolean {
  const all = loadRecords();
  const i = all.findIndex((r) => r.id === rec.id);
  if (i >= 0) all[i] = rec;
  else all.push(rec);
  return saveRecords(all);
}

export function deleteRecord(id: string) {
  saveRecords(loadRecords().filter((r) => r.id !== id));
}

export interface PracticePrefs {
  detectTf: string;
}
export function loadPrefs(): Partial<PracticePrefs> {
  try {
    return JSON.parse(uGet(PREFS) || "{}");
  } catch {
    return {};
  }
}
export function savePrefs(p: Partial<PracticePrefs>) {
  uSet(PREFS, JSON.stringify({ ...loadPrefs(), ...p })); // synced to the cloud
}

// ---- export (for model training) ----
function download(name: string, mime: string, text: string) {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function stamp(): string {
  return new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
}

export function exportJSON(recs: PracticeRecord[]) {
  download(`practice-${stamp()}.json`, "application/json", JSON.stringify(recs, null, 2));
}

const CSV_COLS: [string, (r: PracticeRecord) => unknown][] = [
  ["id", (r) => r.id],
  ["marked_by", (r) => r.markedBy],
  ["shown_at", (r) => (r.shownAt ? new Date(r.shownAt).toISOString() : "")],
  ["decided_at", (r) => new Date(r.createdAt).toISOString()],
  ["decide_seconds", (r) => (r.shownAt ? Math.round((r.createdAt - r.shownAt) / 1000) : "")],
  ["edited_at", (r) => (r.editedAt ? new Date(r.editedAt).toISOString() : "")],
  ["engine", (r) => r.engine],
  ["symbol", (r) => r.symbol],
  ["detect_tf", (r) => r.detectTf],
  ["signal_time", (r) => new Date(r.zone.signalTime * 1000).toISOString()],
  ["signal_unix", (r) => r.zone.signalTime],
  ["type", (r) => r.zone.type],
  ["leg_outs", (r) => r.zone.n],
  ["boring", (r) => r.zone.b],
  ["star_w", (r) => (r.zone.w ? 1 : 0)],
  ["star_e", (r) => (r.zone.e ? 1 : 0)],
  ["side", (r) => r.zone.side],
  ["proximal", (r) => r.zone.proximal],
  ["distal", (r) => r.zone.distal],
  ["atr", (r) => r.zone.atr],
  ["auto_entry", (r) => r.auto.entry],
  ["auto_stop", (r) => r.auto.stop],
  ["auto_target", (r) => r.auto.target],
  ["entry", (r) => r.final.entry],
  ["stop", (r) => r.final.stop],
  ["target", (r) => r.final.target],
  ["adjusted", (r) => (r.adjusted ? 1 : 0)],
  ["rr", (r) => r.rrFinal],
  ["decision", (r) => r.decision],
  ["original_decision", (r) => r.originalDecision ?? r.decision],
  ["previewed", (r) => (r.previewed ? 1 : 0)],
  ["note", (r) => r.note],
  ["outcome", (r) => r.result?.outcome ?? ""],
  ["filled", (r) => (r.result ? (r.result.filled ? 1 : 0) : "")],
  ["bars_to_fill", (r) => r.result?.barsToFill ?? ""],
  ["bars_in_trade", (r) => r.result?.barsInTrade ?? ""],
  ["fill_price", (r) => r.result?.fillPrice ?? ""],
  ["exit_price", (r) => r.result?.exitPrice ?? ""],
  ["r_multiple", (r) => r.result?.rMultiple ?? ""],
  ["pnl_points", (r) => r.result?.pnlPoints ?? ""],
  ["mfe_r", (r) => r.result?.mfeR ?? ""],
  ["mae_r", (r) => r.result?.maeR ?? ""],
];

function csvCell(v: unknown): string {
  const s = v == null ? "" : String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function exportCSV(recs: PracticeRecord[]) {
  const lines = [CSV_COLS.map(([h]) => h).join(",")];
  for (const r of recs) lines.push(CSV_COLS.map(([, f]) => csvCell(f(r))).join(","));
  download(`practice-${stamp()}.csv`, "text/csv", lines.join("\n"));
}
